import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import MaterialModal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import Button from '../atoms/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import CloseButton from '../atoms/CloseButton';
import { COLOR_BACKGROUND_LIGHT, COLOR_BLACK, COLOR_DARK_GRAY, COLOR_SLATE_NAVY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import SubmitButton from '../atoms/SubmitButton';

interface ForgetPasswordDialogProps {
  open: boolean;
  resetPasswordActions?: ResetPasswordAction;
  children?: any;
  onClose: () => void;
}

interface ResetPasswordAction {
  onclick: () => void;
  disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  backdrop: {
    background: 'rgba(0, 0, 0, 0.7)',
  },

  closeButton: {
    border: `1px solid ${COLOR_BLACK}`,
    borderRadius: '50%',
    color: COLOR_BLACK,
    height: 20,
    padding: 0,
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    width: 20,
    zIndex: 2,
  
    '&:after': {
      content: '""',
      position: 'absolute',
      height: '10px',
      width: '1px',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      backgroundColor: 'currentColor',
    },
  
    '&:before': {
      content: '""',
      position: 'absolute',
      height: '10px',
      width: '1px',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(45deg)',
      backgroundColor: 'currentColor',
    },
  
    '&:hover': {
      color: COLOR_BLACK,
    },
  },    

  cancelAction: {
    background: `linear-gradient(to left, ${COLOR_WHITE} 50%, ${COLOR_SLATE_NAVY} 50%)`,
    backgroundColor: COLOR_WHITE,
    backgroundSize: '200% 100%',
    backgroundPosition: 'right bottom',
    border: '3px solid #3a3a3a',
    borderRadius: 100,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 800,
    minHeight: 54,
    minWidth: 185,
    maxWidth: 185,
    padding: theme.spacing(1.25, 2.5),
    transition: 'background-position 0.5s ease-out',
    width: '120px',

    '&:hover': {
      backgroundColor: COLOR_WHITE,
      color: COLOR_WHITE,
      border: '2px solid',
    },
  },

  forgetPasswordBtn: {
    border: '3px solid #3a3a3a',
    borderRadius: 100,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 800,
    minHeight: 54,
    minWidth: 185,
    maxWidth: 185,
    padding: theme.spacing(1.25, 2.5),

    '&:hover': {
      border: `2px solid ${COLOR_BLACK}`,
    }
  },

  root: {
    borderRadius: 0,
    padding: theme.spacing(5),
    maxWidth: '560px',
    position: 'relative',

    '& .MuiCardActions-root': {
      justifyContent: 'space-between',
      padding: theme.spacing(1, 0)
    },
  },

  mediaWrapper: {
    margin: '0 auto 30px auto',
    maxWidth: 200,
    position: 'relative',
  },

  media: {
    backgroundSize: '200px auto',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    height: 100,
    marginTop: 10,
  },

  cardContent: {
    padding: 0,
    '& h2': {
      color: COLOR_TEXT,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(48),
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: '0 0 20px 0',
      paddingBottom: theme.spacing(2.5),
      textAlign: 'center',
    },
    '& p': {
      color: COLOR_TEXT,
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 600,
      lineHeight: 1.2,
      margin: theme.spacing(3.75, 0),
      textAlign: 'center',
    },
  },

  [MEDIA.MOBILE]: {
    closeButton: {
      top: 0,
      right: 0,
    },
  },
  cardInnerContainer: {
    margin: theme.spacing(0, 2.5),
    maxWidth: 400,
  }
}), { name: ForgetPasswordDialog.name });

export default function ForgetPasswordDialog(props: ForgetPasswordDialogProps) {
  const {
    open = false,
    resetPasswordActions = { onclick: () => {}, disabled: false },
    children,
    onClose,
  } = props;
  const classes = useStyles();

  return (
    <MaterialModal
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      className={clsx(classes.dialog)}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        className: classes.backdrop
      }}
      onBackdropClick={onClose}
    >
      <Fade in={open}>
        <Card className={classes.root}>
          <div className={classes.cardInnerContainer}>
            <CloseButton
              className={classes.closeButton}
              onClick={onClose}
            />
            <div className={classes.mediaWrapper}>
              <CardMedia
                className={classes.media}
                image="/images/tracking-football-logo-text.svg"
                title="Logo"
              />
            </div>
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h5" component="h2">
                Forgot Password
              </Typography>
              <div>
                {children}
              </div>
            </CardContent>
            <CardActions>
              {/* <Button
                className={classes.cancelAction}
                onClick={onClose}
              >
                Cancel
              </Button> */}
              <SubmitButton
                text={'Cancel'}
                className={classes.cancelAction}
                onSubmit={onClose}
              />
              <SubmitButton
                text={'Reset Password'}
                className={classes.forgetPasswordBtn}
                onSubmit={resetPasswordActions.onclick}
                disabled={resetPasswordActions.disabled}
              />
            </CardActions>
          </div>
        </Card>
      </Fade>
    </MaterialModal>
  );
}
